@let hasShiftUpdatePermissions =
  can(RoleAction.Update, RolePermissionSubject.Shift);
@let hasServiceUpdatePermissions =
  can(RoleAction.Update, RolePermissionSubject.Service);
@let hasProjectReadPermission =
  can(RoleAction.Read, RolePermissionSubject.Project);
@let shiftColorOption = shiftColorOption$ | async;
@let project = project$ | async; @let label = label$ | async;

@if (
  !updatingShiftIds ||
  updatingShiftIds.indexOf(shiftView?.shift?.id || '') === -1
) {
  @if (
    {
      isAssignmentMode: dragAndDropModeService.isAssignmentMode$ | async,
      isMoveMode: dragAndDropModeService.isMoveMode$ | async,
      shiftIdsToHighlight: shiftIdsToHighlight$ | async,
      areActivitiesVisible: areActivitiesVisible$ | async,
      staffLayoutStyle: staffLayoutStyle$ | async,
    };
    as data
  ) {
    @let rowsActivities =
      data.areActivitiesVisible
        ? ((determinedShiftActivities$ | async) || [] | rowsToActivities)
        : [];
    @let leftPixelPosition = leftPixelPosition$ | async;
    @let rightPixelPosition = rightPixelPosition$ | async;
    @let calculatedShiftWidth =
      { left: leftPixelPosition, right: rightPixelPosition || 0 }
        | timeDifferenceWidthPx
        | async;
    @let isMinimalHeight = data.staffLayoutStyle === StaffLayoutStyle.Minimal;
    @let showActivities = data.areActivitiesVisible || false;
    @let isShiftHighlighted =
      !!data.shiftIdsToHighlight?.includes(shiftView.shift.id);
    @let isShiftVisible =
      timelineShiftDragAndDropDirectiveHelperService.isShiftVisible$ | async;
    @let isShiftDraggable = shiftView.shift | isShiftDraggable;
    @let isFiltered = !shiftView.shift.isHighlighted;
    @let isDragging = draggingItemId === shiftView.shift.id;
    @let frameLength = timelineWidthPixel$ | async;
    @let timeframe = interval$ | async;
    @defer (on hover) {
      <wilson-assignment-shift-card
        wilsonTimelineRowDragHandlerV2
        [numberOfInnerRows]="numberOfInnerRows"
        [innerRowIndex]="innerRowIndex"
        [hasShiftUpdatePermissions]="!!hasShiftUpdatePermissions"
        [hasServiceUpdatePermissions]="!!hasServiceUpdatePermissions"
        [class]="IGNORE_ON_CLICK_CLASS_NAME"
        [shift]="shiftView.shift"
        [shiftId]="shiftView.shift.id"
        [shiftState]="shiftView.shift.state"
        [publicationStatus]="shiftView.shift.publicationStatus"
        [isShiftCompleted]="
          shiftView.shift.operationalStatus === OperationStatus.Completed
        "
        [isShiftStarted]="
          shiftView.shift.operationalStatus === OperationStatus.Ongoing
        "
        [shiftCategoryId]="shiftView.shift.shiftCategoryId"
        [shiftName]="shiftView.shift.name"
        [showShiftValidation]="showShiftValidation"
        [isShiftDraggable]="isShiftDraggable"
        [isDragging]="isDragging"
        [isAssignmentMode]="data.isAssignmentMode"
        [isMoveMode]="data.isMoveMode"
        [isFiltered]="isFiltered"
        [shiftHeight]="shiftHeight"
        [isTooltipVisible]="
          timelineShiftDragAndDropDirectiveHelperService.isTooltipVisible$
            | async
        "
        [isShiftVisible]="isShiftVisible"
        [shiftStartDetail]="shiftStartDetail"
        [shiftEndDetail]="shiftEndDetail"
        [plannedShiftStartDateTime]="shiftView.shift.startDatetime"
        [plannedShiftEndDateTime]="shiftView.shift.endDatetime"
        [plannedStartLocation]="shiftView.shift.startLocation || null"
        [plannedEndLocation]="shiftView.shift.endLocation || null"
        [reportedStartLocation]="shiftView.shift.reportedStartLocation || null"
        [reportedEndLocation]="shiftView.shift.reportedEndLocation || null"
        [leftPixelPosition]="leftPixelPosition"
        [rightPixelPosition]="rightPixelPosition"
        [isHighlighted]="isShiftHighlighted"
        [frameLength]="frameLength"
        [timeframe]="timeframe"
        [showActivities]="showActivities"
        [isActivityDraggable]="true"
        [rowsActivities]="rowsActivities"
        [isMinimalHeight]="isMinimalHeight"
        [calculatedShiftWidth]="calculatedShiftWidth"
        [project]="project"
        [label]="label"
        [loadIconsImmediately]="true"
        (previewRef)="setPreviewRef(dragImage)"
        (selectActivity)="selectActivityEvent.emit($event)"
        (startActivityDrag)="
          ganttActivityDragHandlerHelperService.startDrag($event);
          closeShiftDrawerOnDragOrResize()
        "
        (dragging)="
          ganttActivityDragHandlerHelperService.setDemarcatorRange($event)
        "
        (endActivityDrag)="ganttActivityDragHandlerHelperService.dragEnd()"
        (setSnappingForResize)="
          ganttActivityResizeHandlerHelperService.setSnapping($event)
        "
        (fillActivityGapLeft)="
          ganttActivityResizeHandlerHelperService.fillActivityGapLeft($event)
        "
        (handleResizeLeft)="
          ganttActivityResizeHandlerHelperService.onMouseDown($event, false);
          closeShiftDrawerOnDragOrResize()
        "
        (fillActivityGapRight)="
          ganttActivityResizeHandlerHelperService.fillActivityGapRight($event)
        "
        (handleResizeRight)="
          ganttActivityResizeHandlerHelperService.onMouseDown($event, true);
          closeShiftDrawerOnDragOrResize()
        "
        [isSelectionEnabled]="true"
      ></wilson-assignment-shift-card>
    } @placeholder {
      <div class="preview-shift-container">
        <ng-template #activitiesContentTemplate>
          <ng-container
            *ngTemplateOutlet="
              deferredAssignmentShiftCardHelperService.getActivitiesContentTemplate();
              context: {
                showActivities: showActivities,
                calculatedShiftWidth: calculatedShiftWidth
                  ? calculatedShiftWidth -
                    SHIFT_WIDTH_ADJUSTMENT_FOR_ACTIVITIES_PX
                  : 0,
                isDragging: isDragging,
                rowsActivities: rowsActivities,
                shiftStartDetail: shiftStartDetail,
                shiftEndDetail: shiftEndDetail,
                publicationStatus: shiftView.shift.publicationStatus,
                isActivityDraggable: false,
                frameLength: frameLength,
                timeframe: timeframe,
                userId: shiftView.shift.userId,
                isMoveMode: data.isMoveMode,
                isMinimalHeight: isMinimalHeight,
              }
            "
          >
          </ng-container>
        </ng-template>
        <ng-container
          *ngTemplateOutlet="
            deferredAssignmentShiftCardHelperService.getPreviewTemplate();
            context: {
              shift: shiftView.shift,
              isMinimalHeight: isMinimalHeight,
              showActivities: showActivities,
              leftPixelPosition: leftPixelPosition,
              calculatedShiftWidth: calculatedShiftWidth,
              shiftColorOption: shiftColorOption,
              label: label,
              project: project,
              shiftCategoryId: shiftView.shift.shiftCategoryId,
              hasProjectReadPermission: hasProjectReadPermission,
              showShiftValidation: showShiftValidation,
              hasShiftBeenValidated: !!(
                shiftCardTooltipStateService.hasShiftBeenValidated$ | async
              ),
              isLoadingValidations:
                !!(
                  shiftCardTooltipStateService.isLoadingValidationsForUser$
                  | async
                ) ||
                !!(
                  shiftCardTooltipStateService.isShiftValidationLoading$ | async
                ),
              isLoadingValidationsError: !!(
                shiftCardTooltipStateService.isLoadingValidationsError$ | async
              ),
              isShiftValid: shiftCardTooltipStateService.isShiftValid$ | async,
              isShiftCompleted:
                shiftView.shift.operationalStatus === OperationStatus.Completed,
              isShiftStarted:
                shiftView.shift.operationalStatus === OperationStatus.Ongoing,
              shiftStartDetail: shiftStartDetail,
              shiftEndDetail: shiftEndDetail,
              isHighlighted: isShiftHighlighted,
              numberOfInnerRows: numberOfInnerRows,
              innerRowIndex: innerRowIndex,
              shiftHeight: shiftHeight,
              isShiftDraggable: isShiftDraggable,
              isFiltered: isFiltered,
              isDragging: isDragging,
              isShiftVisible: !!isShiftVisible,
              loadIconsImmediately: !!(loadIconsImmediately$ | async),
              activityDisplayTemplate: activitiesContentTemplate,
              areActivitiesOverlapping: rowsActivities.length > 1,
              contentTemplate: null,
              isShiftSelected: false,
            }
          "
        ></ng-container>
      </div>
    }
  }
  <div id="move-activity" class="move-preview" #dragImage></div>
} @else {
  <wilson-loading-shift-card
    [shift]="shiftView.shift"
    [shiftStartTime]="shiftStartDetail.date"
    [shiftEndTime]="shiftEndDetail.date"
    [project]="project"
    [label]="label"
  ></wilson-loading-shift-card>
}
